import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AppHeader from './AppHeader';
import { useViewportHeight } from './hooks/useViewportHeight';
import { useLanguage } from './LanguageContext';
import { el as elStrings, en as enStrings } from './i18n/app';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const Dashboard = ({ user, token, onLogout, onSelectProject, onUserManagement, onProjectsLoaded, onProfileEdit, onOrgSettings, onSuperAdmin, onSwitchOrg, onViewProjectReadOnly, onCreateOrg }: {
  user: any; token: string; onLogout: () => void;
  onSelectProject: (project: any) => void;
  onUserManagement: () => void;
  onProjectsLoaded: (projects: any[]) => void;
  onProfileEdit: () => void;
  onOrgSettings?: () => void;
  onSuperAdmin?: () => void;
  onSwitchOrg?: (orgId: string) => void;
  onViewProjectReadOnly?: (project: any) => void;
  onCreateOrg?: () => void;
}) => {
  const [projects, setProjects] = useState<any[]>([]);
  const [guestSections, setGuestSections] = useState<any[]>([]);
  const [projectStats, setProjectStats] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newProject, setNewProject] = useState({ name: '' });
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [creating, setCreating] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepProgress, setStepProgress] = useState(0);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState<string | null>(null);
  const [deleteProject, setDeleteProject] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [archiveProject, setArchiveProject] = useState<any>(null);
  const [archiveStep, setArchiveStep] = useState<'confirm' | 'lessons'>('confirm');
  const [ragNotification, setRagNotification] = useState<{ projectName: string; similarProjects: any[] } | null>(null);
  const [lessons, setLessons] = useState({
    projectType: '', industry: '', rating: 5, tags: [] as string[],
    whatWentWell: [''] as string[], whatWentWrong: [''] as string[],
    keyLessons: [''] as string[], whatWouldDoDifferently: [''] as string[],
  });
  const [archiving, setArchiving] = useState(false);
  const [showPortfolioSearch, setShowPortfolioSearch] = useState(false);
  const [portfolioQuery, setPortfolioQuery] = useState('');
  const [portfolioResults, setPortfolioResults] = useState<any[]>([]);
  const [portfolioSearching, setPortfolioSearching] = useState(false);
  const [portfolioMode, setPortfolioMode] = useState<'ai' | 'fallback' | 'empty' | null>(null);
  const [viewingLesson, setViewingLesson] = useState<any | null>(null);
  const [orgKeySet, setOrgKeySet] = useState(true);
  const [showKeyHelp, setShowKeyHelp] = useState(false);
  const [archiveExpanded, setArchiveExpanded] = useState(false);
  const [guestExpanded, setGuestExpanded] = useState(false);
  const [draggingProjectId, setDraggingProjectId] = useState<string | null>(null);
  const [dropTargetActive, setDropTargetActive] = useState(false);
  const [maxProjects, setMaxProjects] = useState<number | null>(null);

  const openNewProjectModal = () => {
    if (['admin', 'org_admin'].includes(user.role)) {
      axios.get(`${API}/organizations/my/settings`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setOrgKeySet(r.data.openai_api_key_set ?? true))
        .catch(() => {});
    }
    setShowModal(true);
  };
  const menuRef = useRef<HTMLDivElement>(null);

  const canCreateProject = ['admin', 'pm', 'pmo'].includes(user.role);
  const canManageUsers = ['admin', 'pm', 'pmo'].includes(user.role);

  useEffect(() => {
    fetchProjects();
    if (['admin', 'org_admin', 'pm', 'pmo'].includes(user.role)) {
      axios.get(`${API}/organizations/my/settings`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setMaxProjects(r.data.max_projects ?? null))
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Εκτιμώμενη διάρκεια κάθε step σε ms
  const STEP_DURATIONS = [3000, 8000, 50000, 5000];
  useEffect(() => {
    if (currentStep < 1 || currentStep > 4) { setStepProgress(0); return; }
    setStepProgress(0);
    const duration = STEP_DURATIONS[currentStep - 1];
    const tick = 80;
    const inc = (tick / duration) * 100;
    const timer = setInterval(() => {
      setStepProgress(prev => {
        const next = prev + inc;
        if (next >= 95) { clearInterval(timer); return 95; }
        return next;
      });
    }, tick);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      const { home, guest } = response.data;
      const homeProjects = home || [];
      const guestData = guest || [];
      setProjects(homeProjects);
      setGuestSections(guestData);
      onProjectsLoaded(homeProjects);
      fetchAllStats([...homeProjects, ...guestData.flatMap((g: any) => g.projects || [])]);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchAllStats = async (projs: any[]) => {
    const statsMap: Record<string, any> = {};
    await Promise.all(projs.map(async (p) => {
      try {
        const [risksRes, issuesRes, scheduleRes, membersRes] = await Promise.allSettled([
          axios.get(`${API}/modules/${p.id}/Risk%20Management`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/modules/${p.id}/Issues`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/modules/${p.id}/Schedule`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/modules/${p.id}/Organization%20Chart`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const risks = risksRes.status === 'fulfilled' ? risksRes.value.data?.data?.risks || [] : [];
        const issues = issuesRes.status === 'fulfilled' ? issuesRes.value.data?.data?.issues || [] : [];
        const schedule = scheduleRes.status === 'fulfilled' ? scheduleRes.value.data?.data || null : null;
        const members = membersRes.status === 'fulfilled' ? membersRes.value.data?.data?.members || [] : [];
        const openIssues = issues.filter((i: any) => i.status !== 'Closed' && i.status !== 'Resolved').length;
        const highRisks = risks.filter((r: any) =>
          (r.risk_type || 'threat') === 'threat' && r.probability > 0.66 && r.impact > 6.66
        ).length;
        const charter = p.ai_analysis?.project_charter || null;
        const budget = p.ai_analysis?.budget || null;
        statsMap[p.id] = { risks, issues, openIssues, highRisks, schedule, members, charter, budget, hasRisks: risks.length > 0, hasIssues: issues.length > 0, hasSchedule: !!schedule?.phases?.length, hasBudget: !!budget?.total, hasCharter: !!charter };
      } catch {
        statsMap[p.id] = { openIssues: 0, highRisks: 0, members: [], hasRisks: false, hasIssues: false, hasSchedule: false, hasBudget: false, hasCharter: false };
      }
    }));
    setProjectStats(statsMap);
  };

  const { language } = useLanguage();
  const L = language === 'en' ? enStrings : elStrings;

  const getProjectHealth = (stats: any) => {
    if (!stats) return { label: '—', color: '#9ca3af', bg: '#f3f4f6', dot: '#9ca3af' };
    if (stats.highRisks >= 3 || stats.openIssues >= 5) return { label: L.dashboard.healthProblem, color: '#991b1b', bg: '#fee2e2', dot: '#ef4444' };
    if (stats.highRisks >= 1 || stats.openIssues >= 2) return { label: L.dashboard.healthRisk, color: '#854d0e', bg: '#fef9c3', dot: '#f59e0b' };
    return { label: L.dashboard.healthOk, color: '#166634', bg: '#dcfce7', dot: '#22c55e' };
  };

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) { setError(L.dashboard.errorRequired); return; }
    if (!contractFile) { setError(L.dashboard.errorNoContract); return; }
    setCreating(true); setError(''); setCurrentStep(1);
    try {
      const projectResponse = await axios.post(`${API}/projects`, newProject, { headers: { Authorization: `Bearer ${token}` } });
      const project = projectResponse.data;
      setAiProcessing(true);
      setCurrentStep(2);
      const formData = new FormData();
      formData.append('uploadedBy', user.id);
      if (contractFile) formData.append('contract', contractFile);
      await new Promise(r => setTimeout(r, 300));
      setCurrentStep(3);
      let uploadRes;
      try {
        uploadRes = await axios.post(`${API}/documents/${project.id}/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
      } catch (uploadErr: any) {
        // Rollback: delete the project if document analysis failed
        await axios.delete(`${API}/projects/${project.id}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
        const msg = uploadErr?.response?.data?.message;
        throw new Error(typeof msg === 'string' ? msg : 'upload_failed');
      }
      setCurrentStep(4);
      await new Promise(r => setTimeout(r, 600));
      const similarProjects = uploadRes.data?.ai_analysis?.similarProjects || [];
      if (similarProjects.length > 0) setRagNotification({ projectName: newProject.name, similarProjects });
      setNewProject({ name: '' });
      setContractFile(null);
      setShowModal(false); setAiProcessing(false); setCurrentStep(0);
      fetchProjects();
    } catch (err: any) {
      const msg: string = err?.response?.data?.message ?? '';
      setError(msg || (err?.message !== 'upload_failed' ? err?.message : null) || L.dashboard.errorCreate);
      setAiProcessing(false); setCurrentStep(0);
    } finally { setCreating(false); }
  };

  const handleDeleteProject = async () => {
    if (deleteConfirm !== L.dashboard.deleteConfirmWord) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/projects/${deleteProject.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setDeleteProject(null); setDeleteConfirm('');
      fetchProjects();
    } catch (err) { console.error(err); }
    finally { setDeleting(false); }
  };

  const handleArchiveProject = async () => {
    setArchiving(true);
    try {
      await axios.post(`${API}/lessons/${archiveProject.id}`, {
        projectName: archiveProject.name,
        projectType: lessons.projectType,
        industry: lessons.industry,
        rating: lessons.rating,
        tags: lessons.tags,
        wentWellItems: lessons.whatWentWell.filter(l => l.trim()),
        wentWrongItems: lessons.whatWentWrong.filter(l => l.trim()),
        keyLessons: lessons.keyLessons.filter(l => l.trim()),
        wouldDoDifferently: lessons.whatWouldDoDifferently.filter(l => l.trim()),
        userId: user.id,
      }, { headers: { Authorization: `Bearer ${token}` } });
      await axios.post(`${API}/lessons/${archiveProject.id}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setArchiveProject(null); resetLessons(); fetchProjects();
    } catch (err) { console.error(err); }
    finally { setArchiving(false); }
  };

  const resetLessons = () => {
    setLessons({
      projectType: '', industry: '', rating: 5, tags: [],
      whatWentWell: [''], whatWentWrong: [''], keyLessons: [''], whatWouldDoDifferently: [''],
    });
    setArchiveStep('confirm');
  };

  const addBullet = (field: 'whatWentWell' | 'whatWentWrong' | 'keyLessons' | 'whatWouldDoDifferently') =>
    setLessons(l => ({ ...l, [field]: [...l[field], ''] }));
  const updateBullet = (field: 'whatWentWell' | 'whatWentWrong' | 'keyLessons' | 'whatWouldDoDifferently', i: number, val: string) =>
    setLessons(l => { const u = [...l[field]]; u[i] = val; return { ...l, [field]: u }; });
  const removeBullet = (field: 'whatWentWell' | 'whatWentWrong' | 'keyLessons' | 'whatWouldDoDifferently', i: number) =>
    setLessons(l => ({ ...l, [field]: l[field].filter((_, idx) => idx !== i) }));

  const handlePortfolioSearch = async () => {
    if (!portfolioQuery.trim()) return;
    setPortfolioSearching(true);
    try {
      const res = await axios.post(
        `${API}/lessons/search`,
        { query: portfolioQuery.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPortfolioResults(res.data.results || []);
      setPortfolioMode(res.data.mode || 'fallback');
    } catch {
      setPortfolioResults([]);
      setPortfolioMode('fallback');
    } finally {
      setPortfolioSearching(false);
    }
  };

  const closePortfolioSearch = () => {
    setShowPortfolioSearch(false);
    setPortfolioQuery('');
    setPortfolioResults([]);
    setPortfolioMode(null);
  };

  const handleOpenProjectReadOnly = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project && onViewProjectReadOnly) {
      closePortfolioSearch();
      setViewingLesson(null);
      onViewProjectReadOnly(project);
    }
  };

  const handleRestoreProject = async (projectId: string) => {
    try {
      await axios.patch(`${API}/projects/${projectId}/restore`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchProjects();
    } catch (err) { console.error(err); }
  };

  const resetModal = () => {
    setShowModal(false); setError('');
    setNewProject({ name: '' });
    setContractFile(null);
  };

  const handleSelectGuestProject = async (project: any, orgId: string) => {
    if (onSwitchOrg) await onSwitchOrg(orgId);
    onSelectProject(project);
  };

  const CHALLENGE_TAGS = [
    { key: 'schedule_delay', el: 'Καθυστέρηση χρονοδιαγράμματος', en: 'Schedule Delay' },
    { key: 'budget_overrun', el: 'Υπέρβαση προϋπολογισμού', en: 'Budget Overrun' },
    { key: 'client_dependency', el: 'Εξαρτήσεις πελάτη', en: 'Client Dependencies' },
    { key: 'team_issues', el: 'Θέματα ομάδας', en: 'Team Issues' },
    { key: 'technical_complexity', el: 'Τεχνική πολυπλοκότητα', en: 'Technical Complexity' },
    { key: 'scope_changes', el: 'Αλλαγές scope', en: 'Scope Changes' },
    { key: 'resource_shortage', el: 'Έλλειψη πόρων', en: 'Resource Shortage' },
    { key: 'communication', el: 'Θέματα επικοινωνίας', en: 'Communication Issues' },
  ];
  const viewportHeight = useViewportHeight();
  const getTagLabel = (key: string) => {
    const tag = CHALLENGE_TAGS.find(t => t.key === key);
    if (!tag) return key;
    return language === 'en' ? tag.en : tag.el;
  };

  return (
    <div style={{ minHeight: viewportHeight, backgroundColor: '#f0f2f5' }}>
      <AppHeader user={user} onProfileEdit={onProfileEdit} onLogout={onLogout} onOrgSettings={onOrgSettings} onSuperAdmin={onSuperAdmin} onCreateOrg={onCreateOrg} />

      {/* Main */}
      <div style={{ padding: '32px', maxWidth: '1600px', margin: '0 auto' }}>

        {/* RAG Notification */}
        {ragNotification && (
          <div style={{ backgroundColor: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '18px' }}>✦</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#4f46e5' }}>
                    {L.dashboard.ragTitle(ragNotification.similarProjects.length, ragNotification.projectName)}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {ragNotification.similarProjects.map((p, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#7c3aed', fontSize: '12px' }}>•</span>
                      <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>{p.name}</span>
                      {p.project_type && <span style={{ fontSize: '11px', color: '#9ca3af' }}>({p.project_type})</span>}
                      <button onClick={() => { const proj = projects.find(pr => pr.id === p.id); if (proj) onSelectProject(proj); }} style={{ fontSize: '11px', color: '#4f46e5', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>{L.dashboard.ragView}</button>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: '12px', color: '#7c3aed', margin: '10px 0 0' }}>{L.dashboard.ragNote}</p>
              </div>
              <button onClick={() => setRagNotification(null)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '18px', flexShrink: 0 }}>✕</button>
            </div>
          </div>
        )}

        {/* Plan limit banner */}
        {canCreateProject && maxProjects !== null && projects.length >= maxProjects && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '12px 18px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '18px' }}>⚠️</span>
              <div>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#92400e' }}>{L.dashboard.planLimitTitle}</p>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#b45309' }}>
                  {['admin', 'org_admin'].includes(user.role)
                    ? <>{L.dashboard.planLimitAdmin(maxProjects!, L.dashboard.planLimitProject(maxProjects!))}</>
                    : <>{L.dashboard.planLimitMember(maxProjects!, L.dashboard.planLimitProject(maxProjects!))}</>
                  }
                </p>
              </div>
            </div>
            {['admin', 'org_admin'].includes(user.role) && onOrgSettings && (
              <button onClick={onOrgSettings} style={{ flexShrink: 0, backgroundColor: '#f59e0b', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {L.dashboard.planLimitUpgrade}
              </button>
            )}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <h2 style={{ fontSize: '26px', fontWeight: '600', color: '#1a1a2e', margin: 0 }}>{L.dashboard.projectsTitle}</h2>
            {canManageUsers && (
              <button style={{ backgroundColor: 'transparent', border: '1px solid #e5e7eb', color: '#374151', padding: '8px 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: '500' }} onClick={onUserManagement}>
                {L.dashboard.manageUsers}
              </button>
            )}
            <button style={{ backgroundColor: 'transparent', border: '1px solid #e5e7eb', color: '#374151', padding: '8px 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: '500' }} onClick={() => setShowPortfolioSearch(true)}>
              {L.dashboard.portfolioSearch}
            </button>
          </div>
          {canCreateProject && (() => {
            const atLimit = maxProjects !== null && projects.length >= maxProjects;
            return (
              <button
                onClick={atLimit ? undefined : openNewProjectModal}
                title={atLimit ? L.dashboard.planLimitTooltip(maxProjects!) : undefined}
                style={{ backgroundColor: atLimit ? '#d1d5db' : '#4f46e5', color: atLimit ? '#9ca3af' : 'white', border: 'none', padding: '12px 24px', borderRadius: '10px', fontSize: '16px', fontWeight: '600', cursor: atLimit ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s' }}>
                {L.dashboard.newProject}
              </button>
            );
          })()}
        </div>

        {loading ? <p style={{ color: '#666' }}>{L.dashboard.loading}</p> : (() => {
          const activeProjects = projects.filter(p => p.status !== 'archived' && p.status !== 'completed');
          const archivedProjects = projects.filter(p => p.status === 'archived' || p.status === 'completed');

          const renderProjectCard = (project: any, isArchived = false) => {
            const stats = projectStats[project.id];
            const health = getProjectHealth(stats);
            return (
              <div
                key={project.id}
                draggable={isArchived}
                onDragStart={isArchived ? () => setDraggingProjectId(project.id) : undefined}
                onDragEnd={isArchived ? () => setDraggingProjectId(null) : undefined}
                onClick={isArchived ? undefined : () => onSelectProject(project)}
                onMouseEnter={e => { if (!isArchived) e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; }}
                style={{
                  backgroundColor: 'white', borderRadius: '12px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)', position: 'relative',
                  overflow: 'visible', border: '1px solid #e5e7eb',
                  opacity: isArchived ? 0.72 : 1,
                  filter: isArchived ? 'grayscale(40%)' : 'none',
                  cursor: isArchived ? 'grab' : 'pointer',
                  transition: 'box-shadow 0.15s, opacity 0.2s',
                }}>
                <div style={{ height: '5px', borderRadius: '12px 12px 0 0', backgroundColor: isArchived ? '#d1d5db' : health.dot }} />
                {isArchived && (
                  <div style={{ position: 'absolute', top: 12, left: 14, fontSize: '11px', fontWeight: '600', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '3px 10px', borderRadius: '999px', letterSpacing: '0.3px' }}>{L.dashboard.archiveBadge}</div>
                )}
                <div style={{ padding: '18px 20px', paddingTop: isArchived ? '32px' : '18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <h3 style={{ fontSize: '17px', fontWeight: '700', color: isArchived ? '#6b7280' : '#1a1a2e', margin: 0, lineHeight: '1.3', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.name}</h3>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0, marginLeft: '10px' }} onClick={e => e.stopPropagation()}>
                      {!isArchived && (
                        <div style={{ position: 'relative' }}>
                          <button onMouseEnter={() => setTooltipOpen(project.id)} onMouseLeave={() => setTooltipOpen(null)} style={{ width: '30px', height: '30px', borderRadius: '50%', border: '1px solid #e5e7eb', backgroundColor: 'white', cursor: 'pointer', fontSize: '15px', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>ⓘ</button>
                          {tooltipOpen === project.id && stats && (
                            <div style={{ position: 'absolute', top: '32px', right: 0, backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', padding: '14px', minWidth: '220px', zIndex: 200 }}>
                              <div style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>{L.dashboard.tooltipTitle}</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: '13px', color: '#6b7280' }}>{L.dashboard.tooltipStatus}</span><span style={{ fontSize: '12px', fontWeight: '600', color: health.color, backgroundColor: health.bg, padding: '2px 8px', borderRadius: '999px' }}>● {health.label}</span></div>
                                {stats.budget?.total && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '13px', color: '#6b7280' }}>{L.dashboard.tooltipBudget}</span><span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>{stats.budget.total}</span></div>}
                                {(() => {
                                  const sch = stats.schedule;
                                  if (!sch) return null;
                                  const allItems = [...(sch.activities || []), ...(sch.milestones || [])];
                                  const maxDay = allItems.reduce((m: number, a: any) => Math.max(m, a.end_day || 0), sch.total_duration_days || 0);
                                  if (!maxDay) return null;
                                  const months = Math.round(maxDay / 30.44);
                                  return <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '13px', color: '#6b7280' }}>Duration</span><span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>{L.dashboard.tooltipDuration(months)}</span></div>;
                                })()}
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '13px', color: '#6b7280' }}>{L.dashboard.tooltipOpenIssues}</span><span style={{ fontSize: '13px', fontWeight: '600', color: stats.openIssues > 0 ? '#f59e0b' : '#1a1a2e' }}>{stats.openIssues}</span></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '13px', color: '#6b7280' }}>{L.dashboard.tooltipHighRisks}</span><span style={{ fontSize: '13px', fontWeight: '600', color: stats.highRisks > 0 ? '#ef4444' : '#1a1a2e' }}>{stats.highRisks}</span></div>
                                {project.project_type && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '13px', color: '#6b7280' }}>{L.dashboard.tooltipType}</span><span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>{project.project_type}</span></div>}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {canCreateProject && !isArchived && (
                        <div style={{ position: 'relative' }} ref={menuOpen === project.id ? menuRef : null}>
                          <button style={{ width: '30px', height: '30px', borderRadius: '6px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontSize: '19px', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === project.id ? null : project.id); }}>···</button>
                          {menuOpen === project.id && (
                            <div style={{ position: 'absolute', top: '100%', right: 0, backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', minWidth: '160px', zIndex: 100, overflow: 'hidden' }}>
                              {project.status === 'active' && <button style={{ display: 'block', width: '100%', padding: '10px 16px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#6366f1', textAlign: 'left' }} onClick={(e) => { e.stopPropagation(); setArchiveProject(project); setArchiveStep('confirm'); setMenuOpen(null); }}>{L.dashboard.menuArchive}</button>}
                              <button style={{ display: 'block', width: '100%', padding: '10px 16px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#ef4444', textAlign: 'left' }} onClick={(e) => { e.stopPropagation(); setDeleteProject(project); setMenuOpen(null); }}>{L.dashboard.menuDelete}</button>
                            </div>
                          )}
                        </div>
                      )}
                      {isArchived && canCreateProject && (
                        <button onClick={(e) => { e.stopPropagation(); handleRestoreProject(project.id); }} title={L.dashboard.restoreTip} style={{ width: '30px', height: '30px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: 'white', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↩</button>
                      )}
                    </div>
                  </div>
                  {project.description && <p style={{ fontSize: '13px', color: '#9ca3af', margin: '0 0 10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.description}</p>}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: '8px' }}>
                    {isArchived
                      ? <span style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>{L.dashboard.dragRestore}</span>
                      : <span style={{ fontSize: '13px', color: '#9ca3af' }}>{L.dashboard.openProject}</span>}
                  </div>
                </div>
              </div>
            );
          };

          return (
            <>
              {/* ── Ενεργά Έργα ── */}
              {activeProjects.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', backgroundColor: 'white', borderRadius: '12px', color: '#666', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                  <p>{L.dashboard.noActive}</p>
                  {canCreateProject && <button style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }} onClick={openNewProjectModal}>{L.dashboard.newProjectFirst}</button>}
                </div>
              ) : (
                <div
                  onDragOver={draggingProjectId ? (e) => { e.preventDefault(); setDropTargetActive(true); } : undefined}
                  onDragLeave={draggingProjectId ? () => setDropTargetActive(false) : undefined}
                  onDrop={draggingProjectId ? (e) => { e.preventDefault(); handleRestoreProject(draggingProjectId); setDraggingProjectId(null); setDropTargetActive(false); } : undefined}
                  style={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px',
                    padding: dropTargetActive ? '16px' : '0',
                    border: dropTargetActive ? '2px dashed #4f46e5' : '2px dashed transparent',
                    borderRadius: '14px', transition: 'all 0.2s',
                    backgroundColor: dropTargetActive ? '#ede9fe20' : 'transparent',
                  }}>
                  {activeProjects.map(p => renderProjectCard(p, false))}
                </div>
              )}

              {/* ── Αρχειοθετημένα Έργα ── */}
              {archivedProjects.length > 0 && (
                <div style={{ marginTop: '32px' }}>
                  <button
                    onClick={() => setArchiveExpanded(e => !e)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: archiveExpanded ? '12px 12px 0 0' : '12px', cursor: 'pointer', transition: 'border-radius 0.2s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '18px' }}>📦</span>
                      <span style={{ fontSize: '17px', fontWeight: '600', color: '#374151' }}>{L.dashboard.archiveSection}</span>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '3px 12px', borderRadius: '999px' }}>{archivedProjects.length}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px', color: '#9ca3af', transition: 'transform 0.2s', display: 'inline-block', transform: archiveExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>⌄</span>
                    </div>
                  </button>
                  {archiveExpanded && (
                    <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 12px 12px', padding: '20px', backgroundColor: '#fafafa' }}>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {L.dashboard.archiveDragHint}
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                        {archivedProjects.map(p => renderProjectCard(p, true))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Έργα Εκτός Οργανισμού ── */}
              {guestSections.length > 0 && (() => {
                const totalGuest = guestSections.reduce((s, g) => s + g.projects.length, 0);
                const allGuestProjects = guestSections.flatMap((g: any) => g.projects);
                return (
                  <div style={{ marginTop: '32px' }}>
                    <button
                      onClick={() => setGuestExpanded(e => !e)}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: guestExpanded ? '12px 12px 0 0' : '12px', cursor: 'pointer', transition: 'border-radius 0.2s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '18px' }}>🌐</span>
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#374151' }}>{L.dashboard.guestSection}</span>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '2px 10px', borderRadius: '999px' }}>{totalGuest}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {!guestExpanded && (
                          <div style={{ display: 'flex', gap: '6px' }}>
                            {allGuestProjects.slice(0, 4).map((p: any) => (
                              <div key={p.id} title={p.name} style={{ width: '28px', height: '28px', borderRadius: '8px', backgroundColor: '#ede9fe', border: '1px solid #c4b5fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: '#7c3aed' }}>
                                {p.name.charAt(0).toUpperCase()}
                              </div>
                            ))}
                            {totalGuest > 4 && <div style={{ width: '28px', height: '28px', borderRadius: '8px', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#9ca3af' }}>+{totalGuest - 4}</div>}
                          </div>
                        )}
                        <span style={{ fontSize: '18px', color: '#9ca3af', transition: 'transform 0.2s', display: 'inline-block', transform: guestExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>⌄</span>
                      </div>
                    </button>
                    {guestExpanded && (
                      <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 12px 12px', padding: '20px', backgroundColor: '#fafafa' }}>
                        {guestSections.map((section: any) => (
                          <div key={section.org_id} style={{ marginBottom: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                              <div style={{ height: '1px', flex: 1, backgroundColor: '#e5e7eb' }} />
                              <span style={{ fontSize: '12px', fontWeight: '600', color: '#7c3aed', backgroundColor: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: '999px', padding: '3px 12px', whiteSpace: 'nowrap' }}>
                                🏢 {section.org_name}
                              </span>
                              <div style={{ height: '1px', flex: 1, backgroundColor: '#e5e7eb' }} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                              {section.projects.map((project: any) => {
                                const stats = projectStats[project.id];
                                const health = getProjectHealth(stats);
                                return (
                                  <div key={project.id} onClick={() => handleSelectGuestProject(project, section.org_id)}
                                    style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #c4b5fd', cursor: 'pointer', overflow: 'hidden', transition: 'box-shadow 0.15s' }}
                                    onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 16px rgba(124,58,237,0.12)')}
                                    onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)')}>
                                    <div style={{ height: '4px', backgroundColor: health.dot }} />
                                    <div style={{ padding: '14px 16px' }}>
                                      <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 4px', lineHeight: 1.3 }}>{project.name}</h3>
                                      {project.description && <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.description}</p>}
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>{L.dashboard.openProject}</span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          );
        })()}
      </div>

      {/* New Project Modal */}
      {showModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>{L.dashboard.newProjectModal}</h2>
            {onOrgSettings && !orgKeySet && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#92400e' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <span>{L.dashboard.noAiKeyMsg}</span>
                  <button onClick={() => { resetModal(); onOrgSettings(); }} style={{ background: 'none', border: 'none', color: '#4f46e5', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>{L.dashboard.addKeyNow}</button>
                </div>
                <button onClick={() => setShowKeyHelp(true)} style={{ background: 'none', border: 'none', padding: 0, marginTop: 4, color: '#b45309', fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>{L.dashboard.howToGetKey}</button>
              </div>
            )}
            <div style={styles.field}><label style={styles.label}>{L.dashboard.fieldTitle}</label><input style={styles.input} value={newProject.name} onChange={e => setNewProject({ ...newProject, name: e.target.value })} placeholder={L.dashboard.titlePlaceholder} /></div>
            <div style={{ backgroundColor: '#f8fafc', borderRadius: '8px', padding: '16px', marginBottom: '20px', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '4px' }}>
                <p style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a2e', margin: 0 }}>{L.dashboard.docSection}</p>
                <span style={{ fontSize: '11px', color: '#9ca3af', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '2px 8px', lineHeight: '1.6' }}>{L.dashboard.docLimit}</span>
              </div>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 14px 0' }}>{L.dashboard.docHint}</p>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>{L.dashboard.contractLabel}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="file" accept=".pdf,.docx" onChange={e => {
                    const file = e.target.files?.[0] || null;
                    if (file && file.size > 30 * 1024 * 1024) {
                      setError(L.dashboard.errorFileTooLarge);
                      e.target.value = '';
                      return;
                    }
                    setContractFile(file);
                  }} style={{ display: 'none' }} id="contract-upload" />
                  <label htmlFor="contract-upload" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', backgroundColor: 'white', border: `1px solid ${contractFile ? '#4f46e5' : '#ddd'}`, borderRadius: '6px', fontSize: '13px', color: contractFile ? '#4f46e5' : '#374151', cursor: 'pointer', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', transition: 'border-color 0.15s' }}>
                    {contractFile ? '📄 ' + contractFile.name : L.dashboard.contractPlaceholder}
                  </label>
                  {contractFile && <button style={{ backgroundColor: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }} onClick={() => setContractFile(null)}>✕</button>}
                </div>
              </div>
            </div>
            {error && <p style={{ color: '#ef4444', fontSize: '14px', marginBottom: '12px' }}>{error}</p>}
            {aiProcessing && (
              <div style={{ backgroundColor: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px' }}>
                {(L.dashboard.loadingSteps as string[]).map((label, idx) => {
                  const stepNum = idx + 1;
                  const done = currentStep > stepNum;
                  const active = currentStep === stepNum;
                  const size = 32;
                  const strokeW = 3;
                  const r = (size - strokeW) / 2;
                  const circ = 2 * Math.PI * r;
                  const offset = active ? circ * (1 - stepProgress / 100) : done ? 0 : circ;
                  return (
                    <div key={stepNum} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: idx < 3 ? '12px' : 0 }}>
                      <svg width={size} height={size} style={{ flexShrink: 0 }}>
                        {/* Track ring */}
                        <circle cx={size/2} cy={size/2} r={r}
                          fill={done ? '#4f46e5' : active ? '#ede9fe' : 'transparent'}
                          stroke={done ? 'none' : active ? '#ddd6fe' : '#e5e7eb'}
                          strokeWidth={strokeW} />
                        {/* Progress ring */}
                        {(active || done) && (
                          <circle cx={size/2} cy={size/2} r={r}
                            fill="none"
                            stroke="#4f46e5"
                            strokeWidth={strokeW}
                            strokeLinecap="round"
                            strokeDasharray={circ}
                            strokeDashoffset={offset}
                            transform={`rotate(-90 ${size/2} ${size/2})`}
                            style={{ transition: active ? 'none' : 'stroke-dashoffset 0.4s ease' }} />
                        )}
                        {/* Label */}
                        <text x={size/2} y={size/2 + 4} textAnchor="middle"
                          fontSize="11" fontWeight="700"
                          fill={done ? 'white' : active ? '#4f46e5' : '#d1d5db'}>
                          {done ? '✓' : stepNum}
                        </text>
                      </svg>
                      <span style={{ fontSize: '14px', fontWeight: active ? 600 : 400, color: done ? '#6b7280' : active ? '#4f46e5' : '#9ca3af', transition: 'all 0.3s' }}>{label}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn} onClick={resetModal}>{L.dashboard.cancel}</button>
              <button style={{ ...styles.createBtn, ...(!orgKeySet ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }} onClick={handleCreateProject} disabled={creating || !orgKeySet}>{creating ? L.dashboard.creating : L.dashboard.createBtn}</button>
            </div>
          </div>
        </div>
      )}

      {/* AI Key Help Modal */}
      {showKeyHelp && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: 'white', borderRadius: 14, padding: '28px 32px', maxWidth: 480, width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#1a1a2e' }}>{L.dashboard.aiKeyTitle}</h3>
              <button onClick={() => setShowKeyHelp(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
            </div>

            {L.dashboard.aiKeySteps.map((step: any, idx: number) => ({
              ...step,
              num: String(idx + 1),
              links: idx === 0 ? [
                { label: 'platform.openai.com', url: 'https://platform.openai.com' },
                { label: 'console.anthropic.com', url: 'https://console.anthropic.com' },
              ] : undefined,
            })).map((step: any) => (
              <div key={step.num} style={{ display: 'flex', gap: 14, marginBottom: 18 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#ede9fe', color: '#4f46e5', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{step.num}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{step.title}</div>
                  <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>{step.body}</div>
                  {step.links && (
                    <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                      {step.links!.map(l => (
                        <a key={l.url} href={l.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#4f46e5', fontWeight: 600, textDecoration: 'none', background: '#ede9fe', padding: '2px 10px', borderRadius: 20 }}>{l.label}</a>
                      ))}
                    </div>
                  )}
                  {'note' in step && step.note && (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#059669', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '4px 10px' }}>{step.note}</div>
                  )}
                </div>
              </div>
            ))}

            <button onClick={() => { setShowKeyHelp(false); resetModal(); onOrgSettings?.(); }} style={{ width: '100%', marginTop: 4, background: '#4f46e5', color: 'white', border: 'none', borderRadius: 8, padding: '10px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {L.dashboard.aiKeyGoSettings}
            </button>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteProject && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}><span style={{ fontSize: '28px' }}>⚠️</span><h2 style={styles.modalTitle}>{L.dashboard.deleteTitle}</h2></div>
            <p style={{ fontSize: '14px', color: '#374151', margin: '0 0 8px 0' }}>{L.dashboard.deleteAbout}</p>
            <p style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 12px 0' }}>"{deleteProject.name}"</p>
            <p style={{ fontSize: '13px', color: '#ef4444', margin: '0 0 24px 0' }}>{L.dashboard.deleteIrreversible}</p>
            <div style={styles.field}><label style={styles.label}>{L.dashboard.deleteConfirmLabel} <strong>{L.dashboard.deleteConfirmWord}</strong>:</label><input style={{ ...styles.input, borderColor: deleteConfirm && deleteConfirm !== L.dashboard.deleteConfirmWord ? '#ef4444' : '#ddd' }} value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder={L.dashboard.deleteConfirmWord} /></div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn} onClick={() => { setDeleteProject(null); setDeleteConfirm(''); }}>{L.dashboard.cancel}</button>
              <button style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', opacity: deleteConfirm === L.dashboard.deleteConfirmWord ? 1 : 0.4, cursor: deleteConfirm === L.dashboard.deleteConfirmWord ? 'pointer' : 'not-allowed' }} onClick={handleDeleteProject} disabled={deleteConfirm !== L.dashboard.deleteConfirmWord || deleting}>{deleting ? L.dashboard.deleting : L.dashboard.deleteBtn}</button>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Search Modal */}
      {showPortfolioSearch && (
        <div style={styles.overlay}>
          <div style={{ ...styles.modal, width: '680px', maxHeight: '84vh', padding: '28px', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div>
                <h2 style={{ ...styles.modalTitle, marginBottom: '4px' }}>{L.dashboard.portfolioTitle}</h2>
                <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{L.dashboard.portfolioSubtitle}</p>
              </div>
              <button onClick={closePortfolioSearch} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>

            {/* Search input */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input
                style={{ ...styles.input, flex: 1 }}
                value={portfolioQuery}
                onChange={e => setPortfolioQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handlePortfolioSearch(); }}
                placeholder={L.dashboard.portfolioSearchPh}
                autoFocus
              />
              <button
                onClick={handlePortfolioSearch}
                disabled={portfolioSearching || !portfolioQuery.trim()}
                style={{ ...styles.createBtn, padding: '10px 20px', opacity: portfolioSearching || !portfolioQuery.trim() ? 0.55 : 1, cursor: portfolioSearching || !portfolioQuery.trim() ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
              >
                {portfolioSearching ? '...' : L.dashboard.portfolioSearchBtn}
              </button>
            </div>

            {/* Results */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {portfolioMode === 'empty' && (
                <div style={{ textAlign: 'center', padding: '48px 24px', color: '#9ca3af' }}>
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>📂</div>
                  <p style={{ fontSize: '14px', margin: 0 }}>{L.dashboard.portfolioNoProjects}</p>
                </div>
              )}
              {portfolioMode !== null && portfolioMode !== 'empty' && portfolioResults.length === 0 && (
                <div style={{ textAlign: 'center', padding: '48px 24px', color: '#9ca3af' }}>
                  <p style={{ fontSize: '14px', margin: 0 }}>{L.dashboard.portfolioNoResults}</p>
                </div>
              )}
              {portfolioResults.map((result, i) => {
                const cardId = result.id || String(i);
                const keyLessons = result.key_lessons || [];
                return (
                  <div key={cardId} style={{ backgroundColor: '#f9fafb', borderRadius: '10px', padding: '16px 18px', marginBottom: '10px', border: '1px solid #e5e7eb' }}>
                    {/* Header row */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a2e', marginBottom: '6px' }}>{result.project_name}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                          {result.rating > 0 && <span style={{ fontSize: '12px', color: '#f59e0b', letterSpacing: '-1px' }}>{'★'.repeat(result.rating)}{'☆'.repeat(5 - result.rating)}</span>}
                          {result.project_type && <span style={{ fontSize: '11px', backgroundColor: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '999px', fontWeight: '500' }}>{result.project_type}</span>}
                          {result.industry && <span style={{ fontSize: '11px', backgroundColor: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '999px', fontWeight: '500' }}>{result.industry}</span>}
                          {(result.tags || []).map((tag: string) => (
                            <span key={tag} style={{ fontSize: '11px', backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '999px', fontWeight: '500' }}>{getTagLabel(tag)}</span>
                          ))}
                        </div>
                      </div>
                      {result.relevance_score && (
                        <span style={{ fontSize: '11px', backgroundColor: '#ede9fe', color: '#4f46e5', padding: '3px 10px', borderRadius: '999px', fontWeight: '700', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {L.dashboard.portfolioMatchScore} {result.relevance_score}/10
                        </span>
                      )}
                    </div>

                    {/* AI match reason */}
                    {result.match_reason && (
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 10px', fontStyle: 'italic', lineHeight: 1.4 }}>"{result.match_reason}"</p>
                    )}

                    {/* Key lessons preview */}
                    {keyLessons.length > 0 && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '5px' }}>{L.dashboard.lessonsKeyLessons}</div>
                        <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#374151', lineHeight: 1.6 }}>
                          {keyLessons.slice(0, 2).map((l: string, idx: number) => <li key={idx}>{l}</li>)}
                          {keyLessons.length > 2 && <li style={{ color: '#9ca3af' }}>+{keyLessons.length - 2} ακόμα...</li>}
                        </ul>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px', alignItems: 'center' }}>
                      {result.is_archived ? (
                        <>
                          <button onClick={() => setViewingLesson(result)} style={{ fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: '5px 14px', borderRadius: '6px', border: '1px solid #4f46e5', backgroundColor: 'white', color: '#4f46e5' }}>
                            Lessons Learned
                          </button>
                          {onViewProjectReadOnly && (
                            <button onClick={() => handleOpenProjectReadOnly(result.project_id)} style={{ fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: '5px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#4f46e5', color: 'white' }}>
                              Άνοιγμα project →
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          <span style={{ fontSize: '11px', backgroundColor: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: '999px', fontWeight: '600' }}>Σε εξέλιξη</span>
                          {result.project_id && (
                            <button
                              onClick={() => {
                                const proj = projects.find(p => p.id === result.project_id);
                                closePortfolioSearch();
                                onSelectProject(proj || { id: result.project_id, name: result.project_name });
                              }}
                              style={{ fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: '5px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#4f46e5', color: 'white' }}
                            >
                              Άνοιγμα project →
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer note */}
            {portfolioMode === 'fallback' && portfolioResults.length > 0 && (
              <div style={{ paddingTop: '10px', textAlign: 'center' }}>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>{L.dashboard.portfolioFallbackNote}</span>
              </div>
            )}
            {portfolioMode === 'ai' && portfolioResults.length > 0 && (
              <div style={{ paddingTop: '10px', textAlign: 'center' }}>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>{L.dashboard.portfolioAiNote}</span>
              </div>
            )}

            <div style={{ ...styles.modalButtons, paddingTop: '14px', marginTop: '4px', borderTop: portfolioMode !== null ? '1px solid #f3f4f6' : 'none' }}>
              <button style={styles.cancelBtn} onClick={closePortfolioSearch}>{L.dashboard.cancel}</button>
            </div>
          </div>
        </div>
      )}

      {/* Viewing Lesson Modal */}
      {viewingLesson && (
        <div style={styles.overlay} onClick={() => setViewingLesson(null)}>
          <div style={{ ...styles.modal, width: '660px', maxHeight: '80vh', padding: '28px 32px', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ ...styles.modalTitle, fontSize: '18px', margin: '0 0 8px' }}>{viewingLesson.project_name}</h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                  {viewingLesson.rating > 0 && (
                    <span style={{ fontSize: '16px', color: '#f59e0b', letterSpacing: '-1px' }}>{'★'.repeat(viewingLesson.rating)}{'☆'.repeat(5 - viewingLesson.rating)}</span>
                  )}
                  {viewingLesson.project_type && <span style={{ fontSize: '12px', backgroundColor: '#dbeafe', color: '#1e40af', padding: '3px 10px', borderRadius: '999px', fontWeight: '500' }}>{viewingLesson.project_type}</span>}
                  {viewingLesson.industry && <span style={{ fontSize: '12px', backgroundColor: '#dcfce7', color: '#166534', padding: '3px 10px', borderRadius: '999px', fontWeight: '500' }}>{viewingLesson.industry}</span>}
                  {(viewingLesson.tags || []).map((tag: string) => (
                    <span key={tag} style={{ fontSize: '12px', backgroundColor: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: '999px', fontWeight: '500' }}>{getTagLabel(tag)}</span>
                  ))}
                </div>
              </div>
              <button onClick={() => setViewingLesson(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1, padding: '0 4px', flexShrink: 0 }}>×</button>
            </div>

            {(() => {
              const wentWell = [...(viewingLesson.went_well_items || []), viewingLesson.what_went_well].filter(Boolean);
              const wentWrong = [...(viewingLesson.went_wrong_items || []), viewingLesson.what_went_wrong].filter(Boolean);
              const keyLessons = viewingLesson.key_lessons || [];
              const wouldDo = viewingLesson.would_do_differently || [];
              const SectionTitle = ({ label }: { label: string }) => (
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase' as const, letterSpacing: '0.5px', marginBottom: '8px' }}>{label}</div>
              );
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {wentWell.length > 0 && (
                    <div>
                      <SectionTitle label={L.dashboard.lessonsWentWell} />
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#374151', lineHeight: 1.7 }}>
                        {wentWell.map((item: string, i: number) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                  {wentWrong.length > 0 && (
                    <div>
                      <SectionTitle label={L.dashboard.lessonsWentWrong} />
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#374151', lineHeight: 1.7 }}>
                        {wentWrong.map((item: string, i: number) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                  {keyLessons.length > 0 && (
                    <div>
                      <SectionTitle label={L.dashboard.lessonsKeyLessons} />
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#374151', lineHeight: 1.7 }}>
                        {keyLessons.map((item: string, i: number) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                  {wouldDo.length > 0 && (
                    <div>
                      <SectionTitle label={L.dashboard.lessonsWouldDo} />
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#374151', lineHeight: 1.7 }}>
                        {wouldDo.map((item: string, i: number) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}

            <div style={{ ...styles.modalButtons, paddingTop: '20px', marginTop: '20px', borderTop: '1px solid #f3f4f6' }}>
              <button style={styles.cancelBtn} onClick={() => setViewingLesson(null)}>{L.dashboard.cancel}</button>
              {onViewProjectReadOnly && projects.find(p => p.id === viewingLesson.project_id) && (
                <button
                  style={{ ...styles.createBtn, backgroundColor: '#4f46e5' }}
                  onClick={() => {
                    setViewingLesson(null);
                    handleOpenProjectReadOnly(viewingLesson.project_id);
                  }}
                >
                  Άνοιγμα project →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      {archiveProject && (
        <div style={styles.overlay}>
          <div style={{ ...styles.modal, width: '560px', maxHeight: '78vh', padding: '24px 28px' }}>
            {archiveStep === 'confirm' ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}><span style={{ fontSize: '28px' }}>📦</span><h2 style={styles.modalTitle}>{L.dashboard.archiveTitle}</h2></div>
                <p style={{ fontSize: '14px', color: '#374151', marginBottom: '8px' }}>{L.dashboard.archiveAbout}</p>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a2e', marginBottom: '12px' }}>"{archiveProject.name}"</p>
                <div style={{ backgroundColor: '#f0f9ff', borderRadius: '8px', padding: '12px', marginBottom: '20px', border: '1px solid #bae6fd' }}><p style={{ fontSize: '13px', color: '#0369a1', margin: 0 }}>{L.dashboard.archiveHint}</p></div>
                <div style={styles.modalButtons}>
                  <button style={styles.cancelBtn} onClick={() => { setArchiveProject(null); resetLessons(); }}>{L.dashboard.cancel}</button>
                  <button style={{ ...styles.createBtn, backgroundColor: '#6366f1' }} onClick={() => setArchiveStep('lessons')}>{L.dashboard.archiveContinue}</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '18px' }}>
                  <h2 style={{ ...styles.modalTitle, fontSize: '17px', margin: '0 0 2px' }}>{L.dashboard.lessonsTitle}</h2>
                  <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>{archiveProject.name}</p>
                </div>

                {/* Project Type + Industry */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                  <div><label style={styles.label}>{L.dashboard.lessonsType}</label><input autoComplete="off" style={styles.input} value={lessons.projectType} onChange={e => setLessons({ ...lessons, projectType: e.target.value })} placeholder={L.dashboard.lessonsTypePlaceholder} /></div>
                  <div><label style={styles.label}>{L.dashboard.lessonsIndustry}</label><input autoComplete="off" style={styles.input} value={lessons.industry} onChange={e => setLessons({ ...lessons, industry: e.target.value })} placeholder={L.dashboard.lessonsIndustryPh} /></div>
                </div>

                {/* Rating */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={styles.label}>
                    {L.dashboard.lessonsRating} <strong style={{ color: '#f59e0b' }}>{'★'.repeat(lessons.rating)}{'☆'.repeat(5 - lessons.rating)}</strong>
                  </label>
                  <input type="range" min="1" max="5" step="1" value={lessons.rating} onChange={e => setLessons({ ...lessons, rating: parseInt(e.target.value) })} style={{ width: '100%', marginTop: '4px', accentColor: '#f59e0b' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}><span>{L.dashboard.lessonsRatingBad}</span><span>{L.dashboard.lessonsRatingMid}</span><span>{L.dashboard.lessonsRatingGood}</span></div>
                </div>

                {/* Challenge Tags */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={styles.label}>{L.dashboard.lessonsTags}</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                    {CHALLENGE_TAGS.map(tag => {
                      const selected = lessons.tags.includes(tag.key);
                      return (
                        <button key={tag.key} onClick={() => setLessons(l => ({ ...l, tags: selected ? l.tags.filter(t => t !== tag.key) : [...l.tags, tag.key] }))} style={{ padding: '5px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', border: `1px solid ${selected ? '#4f46e5' : '#e5e7eb'}`, backgroundColor: selected ? '#ede9fe' : 'white', color: selected ? '#4f46e5' : '#6b7280' }}>
                          {language === 'en' ? tag.en : tag.el}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* What went well */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={styles.label}>{L.dashboard.lessonsWentWell}</label>
                  {lessons.whatWentWell.map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                      <textarea rows={1} autoComplete="off" style={{ ...styles.input, flex: 1, resize: 'none', overflow: 'hidden', lineHeight: '1.5' }} value={item} onChange={e => { updateBullet('whatWentWell', i, e.target.value); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} placeholder={L.dashboard.lessonsWentWellPh} />
                      {lessons.whatWentWell.length > 1 && <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', padding: '0 4px', lineHeight: 1, alignSelf: 'flex-start', marginTop: '10px' }} onClick={() => removeBullet('whatWentWell', i)}>×</button>}
                    </div>
                  ))}
                  <button style={{ background: 'none', border: '1px dashed #4f46e5', color: '#4f46e5', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }} onClick={() => addBullet('whatWentWell')}>{L.dashboard.lessonsAdd}</button>
                </div>

                {/* What didn't go well */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={styles.label}>{L.dashboard.lessonsWentWrong}</label>
                  {lessons.whatWentWrong.map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                      <textarea rows={1} autoComplete="off" style={{ ...styles.input, flex: 1, resize: 'none', overflow: 'hidden', lineHeight: '1.5' }} value={item} onChange={e => { updateBullet('whatWentWrong', i, e.target.value); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} placeholder={L.dashboard.lessonsWentWrongPh} />
                      {lessons.whatWentWrong.length > 1 && <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', padding: '0 4px', lineHeight: 1, alignSelf: 'flex-start', marginTop: '10px' }} onClick={() => removeBullet('whatWentWrong', i)}>×</button>}
                    </div>
                  ))}
                  <button style={{ background: 'none', border: '1px dashed #4f46e5', color: '#4f46e5', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }} onClick={() => addBullet('whatWentWrong')}>{L.dashboard.lessonsAdd}</button>
                </div>

                {/* Key Lessons */}
                <div style={{ marginBottom: '14px' }}>
                  <label style={styles.label}>{L.dashboard.lessonsKeyLessons}</label>
                  {lessons.keyLessons.map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                      <textarea rows={1} autoComplete="off" style={{ ...styles.input, flex: 1, resize: 'none', overflow: 'hidden', lineHeight: '1.5' }} value={item} onChange={e => { updateBullet('keyLessons', i, e.target.value); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} placeholder={L.dashboard.lessonsLessonPh(i)} />
                      {lessons.keyLessons.length > 1 && <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', padding: '0 4px', lineHeight: 1, alignSelf: 'flex-start', marginTop: '10px' }} onClick={() => removeBullet('keyLessons', i)}>×</button>}
                    </div>
                  ))}
                  <button style={{ background: 'none', border: '1px dashed #4f46e5', color: '#4f46e5', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }} onClick={() => addBullet('keyLessons')}>{L.dashboard.lessonsAdd}</button>
                </div>

                {/* What would you do differently */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={styles.label}>{L.dashboard.lessonsWouldDo}</label>
                  {lessons.whatWouldDoDifferently.map((item, i) => (
                    <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                      <textarea rows={1} autoComplete="off" style={{ ...styles.input, flex: 1, resize: 'none', overflow: 'hidden', lineHeight: '1.5' }} value={item} onChange={e => { updateBullet('whatWouldDoDifferently', i, e.target.value); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} placeholder={L.dashboard.lessonsWouldDoPh} />
                      {lessons.whatWouldDoDifferently.length > 1 && <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', padding: '0 4px', lineHeight: 1, alignSelf: 'flex-start', marginTop: '10px' }} onClick={() => removeBullet('whatWouldDoDifferently', i)}>×</button>}
                    </div>
                  ))}
                  <button style={{ background: 'none', border: '1px dashed #4f46e5', color: '#4f46e5', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }} onClick={() => addBullet('whatWouldDoDifferently')}>{L.dashboard.lessonsAdd}</button>
                </div>

                <div style={styles.modalButtons}>
                  <button style={styles.cancelBtn} onClick={() => setArchiveStep('confirm')}>{L.dashboard.lessonsBack}</button>
                  <button style={{ ...styles.createBtn, backgroundColor: '#6366f1' }} onClick={handleArchiveProject} disabled={archiving}>{archiving ? L.dashboard.archiving : L.dashboard.lessonsComplete}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', width: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  field: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333', fontSize: '14px' },
  input: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' as const, outline: 'none', fontFamily: 'inherit' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  createBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
};

export default Dashboard;
